import { useId, useState } from 'react';
import { useCreateTenant } from '../../api/tenants';
import { errorMessage } from '../../api/client';
import type { Tenant } from '../../api/types';
import { Alert, Button, Modal, TextField } from '../ui';

/** The organisation and the owner's address; any staff role can do this. Mounted only while open. */
export function OnboardTenantModal({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (tenant: Tenant) => void;
}) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [email, setEmail] = useState('');
  const [domain, setDomain] = useState('');
  const formId = useId();

  const create = useCreateTenant();
  const error = create.error ? errorMessage(create.error) : null;
  const valid = !!name.trim() && !!slug.trim();

  const close = () => {
    if (!create.isPending) onClose();
  };
  const submit = () => {
    if (!valid || create.isPending) return;
    create.mutate(
      {
        name: name.trim(),
        slug: slug.trim(),
        ...(email.trim() ? { ownerEmail: email.trim() } : {}),
        ...(domain.trim() ? { domain: domain.trim() } : {}),
      },
      { onSuccess: (tenant) => onDone(tenant) },
    );
  };

  return (
    <Modal
      open
      onClose={close}
      title="Onboard a tenant"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={close} disabled={create.isPending}>
            Cancel
          </Button>
          <Button type="submit" form={formId} loading={create.isPending} disabled={!valid}>
            Create tenant
          </Button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      <p className="muted">
        Creates the organisation. No account is created: the owner signs up with the address below
        and is linked to the tenant. You become its account manager; admins can reassign it.
      </p>
      <form
        id={formId}
        className="form form--grid"
        aria-label="Onboard a tenant"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        noValidate
      >
        <TextField
          label="Organization name"
          name="tenantName"
          required
          autoFocus
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (!slugTouched) setSlug(slugify(e.target.value));
          }}
        />
        <TextField
          label="Slug"
          name="tenantSlug"
          required
          value={slug}
          hint="Lowercase letters, digits and hyphens; used in URLs."
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(e.target.value);
          }}
        />
        <TextField
          label="Owner email (optional)"
          name="ownerEmail"
          type="email"
          autoComplete="off"
          value={email}
          hint="Put on the sign-up list; whoever signs up with it becomes the owner. An existing account with it is linked right away."
          onChange={(e) => setEmail(e.target.value)}
        />
        <TextField
          label="Company email domain (optional)"
          name="tenantDomain"
          placeholder="acme.com"
          value={domain}
          hint="Opens sign-up to everyone at the domain and assigns it for email; the domain itself is added on mxroute by hand."
          onChange={(e) => setDomain(e.target.value)}
        />
      </form>
    </Modal>
  );
}

/** "Acme Dental, Inc." → "acme-dental-inc" (the API's slug rule). */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63)
    .replace(/-+$/, '');
}
